import { onbCompleteFlow, type rejectionReason } from '@repo/onb-api';
import { BASE_MOCK_CONFIG, type BaseMockConfig, parseMockConfig as parseBaseConfig } from '@repo/utils/devTools';

// Id of the instance the mock flow-instances GET returns: the panel sets it as the instanceId cookie to resume there
export const MOCK_INSTANCE_ID = 'ad5b0908-9450-426f-b363-20be7482c0b6';

export const FORCE_AUTOCOMPLETE_COOKIE = 'FORCE_AUTOCOMPLETE';

/** Steps the mock instance can be resumed at: the mock flow of this instance type, in order. */
export const START_STEPS = [
  { id: 'default', label: 'Como siempre (instancia mock fija)' },
  ...[...new Set(onbCompleteFlow.steps.map(({ name }) => name))].map((name) => ({ id: name, label: name })),
];

export const SUBMIT_RESULTS = [
  { id: 'next', label: 'Avanza al paso siguiente' },
  { id: 'validationError', label: 'Error de validación en un campo' },
  { id: 'invalidStep', label: 'Paso inválido (INVALID_STEP)' },
  { id: 'stepMismatch', label: 'Paso desfasado (STEP_MISMATCH)' },
  { id: 'rejected', label: 'Rechazo' },
] as const;

export const REJECTION_REASONS: readonly { id: rejectionReason; label: string }[] = [
  { id: 'CUSTOMER_RISK_EVALUATION_REJECTED', label: 'Sin oferta (riesgo rechazado)' },
  { id: 'ORDER_EXCEEDS_APPROVED_AMOUNT', label: 'El carrito supera el monto aprobado' },
  { id: 'MERCHANT_RISK_EVALUATION_REJECTED', label: 'Comercio rechazado por riesgo' },
  { id: 'INVALID_MERCHANT_IDENTIFICATION', label: 'CUIT ya utilizado' },
  { id: 'IDENTITY_VERIFICATION_ATTEMPTS_LIMIT', label: 'Sin intentos de validación de identidad' },
  { id: 'SMS_OTP_VALIDATION_ATTEMPTS_LIMIT', label: 'Sin intentos de código SMS' },
  { id: 'INSUFFICIENT_FUNDS', label: 'Tarjeta sin fondos' },
  { id: 'PAYMENT_GATEWAY_ERROR', label: 'Pago no autorizado' },
  { id: 'TIME_OUT', label: 'Sesión vencida' },
];

export const IDENTITY_RESULTS = [
  { id: 'approved', label: 'Aprobada, avanza' },
  { id: 'pending', label: 'En verificación (pantalla de espera)' },
  { id: 'retry', label: 'Reintento (segundo intento)' },
  { id: 'failed', label: 'Rechazada (no pudimos validar tu identidad)' },
] as const;

export const BIOMETRY_FLOWS = [
  { id: 'FULL_BIOMETRY', label: 'Completa (DNI + selfie)' },
  { id: 'SHORT_BIOMETRY', label: 'Corta (solo selfie)' },
] as const;

export const OFFERS = [
  { id: 'default', label: 'Como siempre' },
  { id: 'low', label: 'Hasta $ 60.000', maximumAmount: 60000 },
  { id: 'high', label: 'Hasta $ 3.000.000', maximumAmount: 3000000 },
] as const;

export const INSTANCE_RESULTS = [
  { id: 'created', label: 'Se crea' },
  { id: 'error', label: 'Servicio externo caído (504)' },
] as const;

export const ORDER_CREDENTIALS = [
  { id: 'valid', label: 'Válida' },
  { id: 'expired', label: 'Vencida o inexistente' },
] as const;

export type MockConfig = BaseMockConfig & {
  // Step the mock instance is on when resumed (GET flow-instances)
  startStep: string;
  submitResult: (typeof SUBMIT_RESULTS)[number]['id'];
  rejectionReason: rejectionReason;
  identity: (typeof IDENTITY_RESULTS)[number]['id'];
  biometry: (typeof BIOMETRY_FLOWS)[number]['id'];
  offer: (typeof OFFERS)[number]['id'];
  instance: (typeof INSTANCE_RESULTS)[number]['id'];
  orderCredential: (typeof ORDER_CREDENTIALS)[number]['id'];
};

// Every default reproduces the mock routes as they were before the dev tools: the Cypress specs run against them
// without a config cookie
export const DEFAULT_MOCK_CONFIG: MockConfig = {
  ...BASE_MOCK_CONFIG,
  startStep: 'default',
  submitResult: 'next',
  rejectionReason: 'CUSTOMER_RISK_EVALUATION_REJECTED',
  identity: 'approved',
  biometry: 'FULL_BIOMETRY',
  offer: 'default',
  instance: 'created',
  orderCredential: 'valid',
};

const known = <T extends string>(all: readonly { id: T }[], value: unknown, fallback: T): T =>
  all.some(({ id }) => id === value) ? (value as T) : fallback;

// A cookie from an older version may hold values that no longer exist
export const parseMockConfig = (value?: string | null): MockConfig =>
  parseBaseConfig(value, DEFAULT_MOCK_CONFIG, (config) => ({
    ...config,
    startStep: known(START_STEPS, config.startStep, DEFAULT_MOCK_CONFIG.startStep),
    submitResult: known(SUBMIT_RESULTS, config.submitResult, DEFAULT_MOCK_CONFIG.submitResult),
    rejectionReason: known(REJECTION_REASONS, config.rejectionReason, DEFAULT_MOCK_CONFIG.rejectionReason),
    identity: known(IDENTITY_RESULTS, config.identity, DEFAULT_MOCK_CONFIG.identity),
    biometry: known(BIOMETRY_FLOWS, config.biometry, DEFAULT_MOCK_CONFIG.biometry),
    offer: known(OFFERS, config.offer, DEFAULT_MOCK_CONFIG.offer),
    instance: known(INSTANCE_RESULTS, config.instance, DEFAULT_MOCK_CONFIG.instance),
    orderCredential: known(ORDER_CREDENTIALS, config.orderCredential, DEFAULT_MOCK_CONFIG.orderCredential),
  }));
